import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService } from '../../service/api.service';
import { AccountService } from '../../service/account.service';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { DatePipe, JsonPipe } from '@angular/common';
import { PowProgressComponent } from '../../helper/pow-progress/pow-progress.component';
import { PowService } from '../../service/pow.service';

@Component({
  selector: 'app-login',
  imports: [PowProgressComponent, MatCardModule, MatButtonModule, MatProgressSpinnerModule, MatIconModule, MatExpansionModule, DatePipe, JsonPipe, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.less'
})
export class LoginComponent {

  logginIn = false;
  loginError: string | null = null;
  loginErrorDetails: any | null = null;
  showErrorDetails = false;

  // Proof of work: the login button stays disabled until powState is set
  powState: string | null = null;
  powError = false;

  route = inject(ActivatedRoute);
  router = inject(Router);
  api = inject(ApiService);
  accountService = inject(AccountService);
  powService = inject(PowService);

  ngOnInit() {
    // Check for code parameter in URL
    this.route.queryParams.subscribe(params => {
      const code = params['code'];
      if (code) {
        console.log('Received authorization code:', code);
        this.handleAuthorizationCode(code, params['state']);
      } else {
        this.startChallenge();
      }
    });
  }

  async startChallenge() {
    this.powState = null;
    this.powError = false;
    try {
      this.powState = btoa(JSON.stringify(await this.powService.solve('login')));
    } catch (e) {
      console.error('Failed to solve login challenge:', e);
      this.powError = true;
    }
  }

  handleAuthorizationCode(code: string, state?: string) {
    this.logginIn = true;
    this.loginError = null;
    this.loginErrorDetails = null;
    this.showErrorDetails = false;
    
    let pow: any = null;
    try {
      pow = JSON.parse(atob(state ?? ''));
    } catch { }

    this.api.post('/account/login.php', { code: code, pow: pow }).subscribe((res: any) => {
      console.log(res);
      this.accountService.loadAccount();
      this.router.navigate(['/']);
    }, (error: any) => {
      console.error('Failed to login:', error);
      this.logginIn = false;
      
      // Handle error response
      if (error.error && error.error.message) {
        this.loginError = error.error.message;
        this.loginErrorDetails = {
          error_type: error.error.error_type || 'unknown',
          debug_info: error.error.debug_info || null,
          timestamp: new Date().toISOString(),
          status: error.status || 'unknown'
        };
      } else {
        this.loginError = 'An unexpected error occurred during login. Please try again.';
        this.loginErrorDetails = {
          error_type: 'network_error',
          debug_info: {
            timestamp: new Date().toISOString(),
            status: error.status || 'unknown',
            message: error.message || 'Network or server error'
          }
        };
      }
    });
  }

  login() {
    if (this.powState) {
      this.accountService.login(this.powState);
    }
  }

  retryLogin() {
    this.loginError = null;
    this.loginErrorDetails = null;
    this.showErrorDetails = false;
    // The old challenge is spent, get a new one
    this.startChallenge();
  }

  toggleErrorDetails() {
    this.showErrorDetails = !this.showErrorDetails;
  }

  copyErrorDetails() {
    const errorText = `Login Error Report
Timestamp: ${this.loginErrorDetails?.timestamp}
Error Type: ${this.loginErrorDetails?.error_type}
Status: ${this.loginErrorDetails?.status}
Message: ${this.loginError}

Debug Information:
${JSON.stringify(this.loginErrorDetails?.debug_info, null, 2)}

Please include this information when reporting the issue to the developers.`;

    navigator.clipboard.writeText(errorText).then(() => {
      // Could add a toast notification here if available
      console.log('Error details copied to clipboard');
    });
  }

}
